import { Info } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import KumocanButton from 'components/button'
import KumocanDialog from 'components/dialog'
import KumocanSimpleTextareaAutosize from 'components/simpleTextArea'
import { assignApi } from 'hooks/api/assign'
import { Assign } from 'types/assign'

type SecurityNoteHandlingState = 'idle' | 'saving' | 'saved'

const useQuery = () => {
  return new URLSearchParams(useLocation().search)
}

export default function TopPage(): JSX.Element {
  const { t } = useTranslation(['page-top', 'common'])
  const query = useQuery()
  const assignId = query.get('assignId') || ''
  const [assign, setAssign] = useState<Assign>()
  const [securityNoteHandlingState, setSecurityNoteHandlingState] =
    useState<SecurityNoteHandlingState>('idle')
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [savedSecurityNote, setSavedSecurityNote] = useState<string>('')

  useEffect(() => {
    if (!assignId) return
    assignApi()
      .findById(assignId)
      .then((res) => {
        setAssign(res.data)
      })
  }, [])

  const throttledInputHandler = useRef(
    throttle(async (value: string) => {
      setSecurityNoteHandlingState('saving')
      await assignApi().updateSecurityNote(assignId, value)
      setSecurityNoteHandlingState('saved')
    }, 2000),
  ) // 2秒間隔でスロットリング

  const handleInput = (value: string) => {
    const trimed = value.trim()
    throttledInputHandler.current(trimed)
  }

  /** 保存された警備員用メモを確認 */
  const handleConfirmSavedNote = async () => {
    if (!assignId) return
    try {
      const response = await assignApi().findById(assignId)
      setSavedSecurityNote(response.data.securityNote || '')
      setIsConfirmDialogOpen(true)
    } catch (error) {
      console.error('Error fetching saved note:', error)
    }
  }

  const handleConfirmDialogClose = () => {
    setIsConfirmDialogOpen(false)
  }

  return (
    <Box sx={{ width: '99%', margin: '10px auto 0px' }}>
      <KumocanSimpleTextareaAutosize
        id="text-area"
        onInput={handleInput}
        placeHolder={t('security_note.placeholder') ?? ''}
        defaultValue={assign?.securityNote}
        style={{
          width: '96%',
          height: '100px',
          fontSize: '20px',
          padding: '5px',
        }}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginTop: '8px',
        }}
      >
        <Info sx={{ fontSize: '18px', color: '#666' }} />
        <Typography sx={{ fontSize: '14px', color: '#666' }}>
          {t(`security_note.status.${securityNoteHandlingState}`)}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right', marginTop: '4px' }}>
        <Typography
          component="span"
          onClick={handleConfirmSavedNote}
          sx={{
            fontSize: '14px',
            color: '#666',
            cursor: 'pointer',
            padding: '4px 0',
            display: 'inline-block',
            textDecoration: 'underline',
          }}
        >
          {t('security_note.check_saved')}
        </Typography>
      </Box>

      {/* 保存内容確認ダイアログ */}
      <KumocanDialog
        open={isConfirmDialogOpen}
        handleClose={handleConfirmDialogClose}
      >
        <Box sx={{ width: '100%' }}>
          <Typography sx={{ fontSize: '20px', fontWeight: 'bold', mb: 2 }}>
            {t('security_note.saved_title')}
          </Typography>
          <Box
            sx={{
              backgroundColor: '#f5f5f5',
              padding: '16px',
              borderRadius: '8px',
              minHeight: '100px',
              maxHeight: '300px',
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <Typography sx={{ fontSize: '16px' }}>
              {savedSecurityNote || t('security_note.no_saved')}
            </Typography>
          </Box>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <KumocanButton
              onClick={handleConfirmDialogClose}
              style={{
                minWidth: '100px',
                height: '40px',
                fontSize: '16px',
              }}
            >
              {t('common:buttons.close')}
            </KumocanButton>
          </Box>
        </Box>
      </KumocanDialog>
    </Box>
  )
}

function throttle(func: (...args: any[]) => void, limit: number) {
  let lastFunc: NodeJS.Timeout
  let lastRan: number

  return function (...args: any[]) {
    if (!lastRan) {
      func(...args)
      lastRan = Date.now()
    } else {
      clearTimeout(lastFunc)
      lastFunc = setTimeout(
        function () {
          if (Date.now() - lastRan >= limit) {
            func(...args)
            lastRan = Date.now()
          }
        },
        limit - (Date.now() - lastRan),
      )
    }
  }
}
